/**
 * 계정 접속 제한 (허용 IP)
 * ---------------------------------------------------------------------------
 * 플랫폼 운영자 계정은 전 고객사에 닿는다. 비밀번호와 2차 인증만으로는
 * "어디서든 들어올 수 있다" 는 성질이 남으므로, 알려진 위치에서만 들어오게 한다.
 *
 * ## 2차 인증을 대신하지 않는다
 * 덧붙이는 것이다. 여기를 통과해도 이메일 인증코드는 그대로 돈다.
 * 순서는 비밀번호 → 허용 IP → 2차 인증이다.
 *
 * ## MAC 주소를 받지 않는 이유
 * 웹 서버는 접속자의 MAC 주소를 볼 수 없다. MAC 은 같은 랜 구간에서만 보이는
 * 2계층 정보라, 공유기를 한 번만 지나도 사라진다. 서버에 도달하는 것은 IP 다.
 */

import { useEffect, useState } from 'react';
import { Alert, Form, Input, Modal, Space, Switch, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { apiClient, type NormalizedError } from '../../lib/api-client';
import { errorText, useApiMutation } from '../../lib/queries';

const { Text, Paragraph } = Typography;

interface AccessRule {
  ipCheckEnabled: boolean;
  allowedCidrs: string[];
  memo: string | null;
}

interface Props {
  open: boolean;
  /** 대상 계정. 닫혀 있으면 null */
  target: { userId: string; name: string; email: string } | null;
  onClose: () => void;
}

export default function UserAccessRuleModal({ open, target, onClose }: Props) {
  const [enabled, setEnabled] = useState(false);
  const [cidrText, setCidrText] = useState('');
  const [memo, setMemo] = useState('');

  const rule = useQuery<AccessRule, NormalizedError>({
    queryKey: ['user-access-rule', target?.userId],
    enabled: open && Boolean(target?.userId),
    queryFn: async () => {
      const { data } = await apiClient.get<AccessRule>(`/users/${target!.userId}/access-rule`);
      return data;
    },
  });

  useEffect(() => {
    if (!rule.data) return;
    setEnabled(rule.data.ipCheckEnabled);
    setCidrText(rule.data.allowedCidrs.join('\n'));
    setMemo(rule.data.memo ?? '');
  }, [rule.data]);

  /** 줄바꿈·쉼표 어느 쪽으로 적어도 받는다. 빈 줄은 버린다. */
  const parseCidrs = (text: string) =>
    text
      .split(/[\n,]/)
      .map((v) => v.trim())
      .filter(Boolean);

  const cidrs = parseCidrs(cidrText);

  const save = useApiMutation<void>(
    async () => {
      await apiClient.put(`/users/${target!.userId}/access-rule`, {
        ipCheckEnabled: enabled,
        allowedCidrs: cidrs,
        memo: memo.trim() || null,
      });
    },
    { onSuccess: () => onClose() },
  );

  // 켜면서 비워 두면 본인도 못 들어온다. 저장을 막는다.
  const emptyWhileEnabled = enabled && cidrs.length === 0;

  return (
    <Modal
      open={open}
      title={target ? `접속 제한 — ${target.name}` : '접속 제한'}
      okText="저장"
      cancelText="취소"
      confirmLoading={save.isPending}
      okButtonProps={{ disabled: emptyWhileEnabled }}
      onOk={() => save.mutate()}
      onCancel={onClose}
      destroyOnHidden
      width={560}
    >
      <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
        {save.error && <Alert type="error" showIcon message={errorText(save.error)} />}

        <Alert
          type="info"
          showIcon
          message="2차 인증은 그대로 적용됩니다"
          description="허용 IP 는 2차 인증을 대신하지 않고 더합니다. 로그인 순서는 비밀번호 → 허용 IP → 이메일 인증코드입니다."
        />

        <Form layout="vertical">
          <Form.Item label="허용 IP 검사">
            <Space>
              <Switch checked={enabled} onChange={setEnabled} />
              <Text>{enabled ? '사용' : '미사용'}</Text>
            </Space>
            <Paragraph type="secondary" style={{ fontSize: 12, marginTop: 6, marginBottom: 0 }}>
              미사용이면 어디서든 로그인할 수 있습니다(기본값). 사용으로 두면 아래 대역에서만
              로그인됩니다.
            </Paragraph>
          </Form.Item>

          <Form.Item
            label="허용 IP / 대역"
            validateStatus={emptyWhileEnabled ? 'error' : undefined}
            help={
              emptyWhileEnabled
                ? '한 개 이상 입력해야 합니다. 비워 두고 켜면 본인도 들어올 수 없습니다.'
                : '한 줄에 하나씩. 단일 IP 는 203.0.113.9 처럼, 대역은 203.0.113.0/24 처럼 적습니다.'
            }
          >
            <Input.TextArea
              rows={5}
              value={cidrText}
              onChange={(e) => setCidrText(e.target.value)}
              placeholder={'203.0.113.0/24\n198.51.100.7'}
              disabled={!enabled}
            />
          </Form.Item>

          <Form.Item label="메모">
            <Input
              value={memo}
              onChange={(e) => setMemo(e.target.value)}
              placeholder="예: 본사 사무실 대역, 관리자 재택 고정IP"
              maxLength={200}
            />
          </Form.Item>
        </Form>

        <Text type="secondary" style={{ fontSize: 12 }}>
          MAC 주소는 받지 않습니다 — 웹 서버에는 접속자의 MAC 이 도달하지 않습니다(같은 랜
          구간에서만 보이는 정보입니다). 기기를 고정하려면 고정 IP 나 VPN 대역을 쓰세요.
        </Text>
      </Space>
    </Modal>
  );
}
